import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { createContact } from '../../firebase/services/contactService';

/**
 * Component for editing or creating contacts
 */
const ContactEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  // State for form fields
  const [email, setEmail] = useState(isEditMode ? 'john.doe@example.com' : '');
  const [firstName, setFirstName] = useState(isEditMode ? 'John' : '');
  const [lastName, setLastName] = useState(isEditMode ? 'Doe' : '');
  const [status, setStatus] = useState(isEditMode ? 'active' : 'active');
  
  // State for form handling
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Handle save action
  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const contactData = {
        email,
        firstName,
        lastName,
        status,
        preferences: {
          emailEnabled: true
        }
      };
      
      await createContact(contactData);
      
      // Navigate back to contacts list
      navigate('../contacts');
    } catch (err) {
      console.error('Error saving contact:', err);
      setError(err instanceof Error ? err.message : 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('../contacts')}
        >
          Back to Contacts
        </Button>
        <Typography variant="h5">
          {isEditMode ? 'Edit Contact' : 'Add New Contact'}
        </Typography>
      </Box>
      
      <Paper sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: '1 1 300px' }}>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              type="email"
            />
          </Box>
          
          <Box sx={{ flex: '1 1 300px' }}>
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
            />
          </Box>
          
          <Box sx={{ flex: '1 1 300px' }}>
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
            />
          </Box>
          
          <Box sx={{ flex: '1 1 300px' }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ width: '100%', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('../contacts')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={loading || !email}
            >
              Save Contact
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ContactEditor; 